// Module 10 — selection HIGHLIGHT: one pooled point primitive that rings the currently selected
// object on the globe, so a click visibly responds AT the object (sim_map.html parity) while the
// inspector sheet opens at the screen edge. Pool discipline (same as flow-dots): the point is
// created once on first use, then repositioned/shown/hidden in place — never added per click.
// Its pick id kind is "sel-marker", which resolvePick rejects, so the ring sitting on top of the
// asset can never swallow the next click. Boundary: same-module only.
import type { CesiumLike, PointPrimitiveCollectionLike, PointPrimitiveLike } from "./cesium-api";

export interface SelectionMarker {
  // sel → look up its world position and ring it; undefined (or an unknown id) → hide the ring.
  set(sel: { kind: string; id: string } | undefined): void;
}

const GLOW_PX = 60; // soft outer halo — the "surrounding light" around any selected object
const RING_PX = 30; // crisp inner ring with a hot rim

type RealPoint = { pixelSize?: number; outlineColor?: unknown; outlineWidth?: number; disableDepthTestDistance?: number };

export function makeSelectionMarker(
  cesium: CesiumLike,
  points: PointPrimitiveCollectionLike,
  // adapter-provided: node coord / edge mid-vertex / packet midpoint as a Cartesian3 (undefined = not locatable)
  lookup: (sel: { kind: string; id: string }) => unknown,
): SelectionMarker {
  let glow: PointPrimitiveLike | null = null; // large soft halo
  let ring: PointPrimitiveLike | null = null; // crisp bright ring

  return {
    set(sel): void {
      const pos = sel ? lookup(sel) : undefined;
      if (pos === undefined) {
        if (glow) glow.show = false;
        if (ring) ring.show = false;
        return;
      }
      if (!ring) {
        // Two stacked points (both pick-id "sel-marker" → resolvePick rejects them, so the halo on top
        // of an asset can never swallow the next click). Both drawn THROUGH terrain/buildings so the
        // selection is never buried. This is the unified halo for EVERY selected kind — node, parcel,
        // plant, substation, neighbourhood, cable, and info/finance packet (anchored by the adapter's
        // lookup). real Cesium props the structural seam doesn't model (the test fake ignores them).
        glow = points.add({
          position: pos, id: { kind: "sel-marker", id: "glow" },
          color: cesium.Color.fromCssColorString("#FF8C1A").withAlpha(0.22), // warm orange glow (theme accent)
        });
        const rg = glow as RealPoint;
        rg.pixelSize = GLOW_PX;
        rg.disableDepthTestDistance = Number.POSITIVE_INFINITY;
        ring = points.add({
          position: pos, id: { kind: "sel-marker", id: "sel" },
          color: cesium.Color.fromCssColorString("#ffffff").withAlpha(0.5),
        });
        const rr = ring as RealPoint;
        rr.pixelSize = RING_PX;
        rr.outlineColor = cesium.Color.fromCssColorString("#F0E442").withAlpha(1.0); // hot rim, full alpha
        rr.outlineWidth = 8;
        rr.disableDepthTestDistance = Number.POSITIVE_INFINITY;
      }
      glow!.position = pos;
      ring!.position = pos;
      glow!.show = true;
      ring!.show = true;
    },
  };
}
